import { AlertTriangle, Scissors } from "lucide-react";

// Dach-Passt-Prüfung: Modulanzahl vs. Obergrenze aus Google Solar / KI-Dachanalyse.
const SOURCE_LABEL = { google_solar: "Google Solar", manual_roof: "manuell eingegebener Dachfläche", ki_analysis: "KI-Dachanalyse" };

export const countModules = (items, catalog) => {
  const byId = Object.fromEntries((catalog || []).map((c) => [c.id, c]));
  return (items || []).reduce((n, it) => n + (byId[it.component_id]?.type === "module" ? Number(it.qty) || 0 : 0), 0);
};

export const exceedsRoof = (modules, limit) => !!limit?.max_panels && modules > limit.max_panels;

// Module auf die Dachgrenze kürzen; Positionen mit exakt der alten Modul-Stückzahl
// (z. B. Montage/Unterkonstruktion pro Modul) werden mitgekürzt.
export const fitItemsToRoof = (items, catalog, max) => {
  const byId = Object.fromEntries((catalog || []).map((c) => [c.id, c]));
  const isModule = (it) => !it.is_free && byId[it.component_id]?.type === "module";
  const mods = items.filter(isModule);
  const total = mods.reduce((n, it) => n + (Number(it.qty) || 0), 0);
  if (!max || total <= max) return items;
  const scaled = new Map(mods.map((it) => [it, Math.floor((Number(it.qty) || 0) * max / total)]));
  let left = max - [...scaled.values()].reduce((a, b) => a + b, 0);
  for (const it of mods) { if (left <= 0) break; scaled.set(it, scaled.get(it) + 1); left -= 1; }
  return items
    .map((it) => (isModule(it) ? { ...it, qty: scaled.get(it) } : Number(it.qty) === total ? { ...it, qty: max } : it))
    .filter((it) => !isModule(it) || it.qty > 0);
};

// Gespeicherte Konfig-Positionen → PATCH-Payload (wie beim Laden in den Konfigurator).
export const toPayloadItems = (items) => (items || []).map((i) => (i.is_free
  ? { is_free: true, title: i.title || "", qty: i.qty || 1, unit_price_net_eur: Number(i.unit_price_net_eur) || 0,
      vat_rate: typeof i.vat_rate === "number" ? i.vat_rate : 0 }
  : { component_id: i.component_id, qty: i.qty || 1,
      ...(typeof i.ref_price_net_eur === "number" && i.ref_price_net_eur > 0 ? { ref_price_net_eur: i.ref_price_net_eur } : {}),
      ...(typeof i.vat_rate === "number" ? { vat_rate: i.vat_rate } : {}) }));

export function RoofFitWarning({ modules, limit, testid, onFit }) {
  if (!exceedsRoof(modules, limit)) return null;
  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 flex flex-wrap items-center gap-2 text-[12.5px] text-amber-100" data-testid={testid}>
      <AlertTriangle size={15} className="text-amber-300 shrink-0" />
      <span className="flex-1 min-w-[12rem]">
        <b>Passt evtl. nicht aufs Dach:</b> {modules} Module gewählt, laut {SOURCE_LABEL[limit.limit_source]} passen max. {limit.max_panels}.
      </span>
      {onFit && (
        <button type="button" onClick={onFit} data-testid={`${testid}-fit`}
          className="w-full sm:w-auto h-10 sm:h-8 px-3 rounded-lg bg-amber-500 text-zinc-900 font-bold text-[12.5px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-400 active:scale-[0.98] transition-transform">
          <Scissors size={13} /> Auf {limit.max_panels} Module kürzen
        </button>
      )}
    </div>
  );
}

export function RoofFitBadge({ modules, limit, testid }) {
  if (!exceedsRoof(modules, limit)) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10.5px] font-semibold"
      title={`${modules} Module – laut ${SOURCE_LABEL[limit.limit_source]} passen max. ${limit.max_panels}`} data-testid={testid}>
      <AlertTriangle size={11} /> {modules}/{limit.max_panels} Module – zu viele fürs Dach
    </span>
  );
}
